import React, { useState, useEffect } from "react";
import { Navigate } from 'react-router-dom';
import { userService } from "../../_services";
import * as z from 'zod';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, CheckCircle, User, Lock } from "lucide-react";
import WisePayment from "../Consumer/WisePayment";

const accountInfoProfileSchema = z.object({
    userName: z.string()
        .refine(val => val.trim().length >= 5, 'Username must be at least 5 characters long')
        .refine(val => !val.includes(' '), 'Username does not allow space'),
    fname: z.string().min(3, 'First Name must be at least 3 characters long'),
    lname: z.string().min(2, 'Last Name must be at least 2 characters long'),
    phone: z.string()
        .refine(val => val === '' || userService.ValidPhone(val), 'Please enter valid Phone Number'),
    email: z.string()
        .refine(val => val.length >= 1, 'Enter your email')
        .refine(val => val === '' || userService.ValidEmailRegex(val), 'Invalid Email'),
    address1: z.string().min(1, 'Please Input street address'),
    city: z.string().min(2, 'Please input city'),
    state: z.string().min(1, 'Please Select State'),
    zip: z.string().length(5, 'Please input valid zipcode'),
});

const accountPasswordSchema = z.object({
    currentPassword: z.string().min(6, 'At least 6 characters in length'),
    newPassword: z.string().min(6, 'At least 6 characters in length'),
    confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
    message: 'Password do not match',
    path: ['confirmPassword'],
});

const Account_Info_page = () => {
    // User profile state
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [fname, setFname] = useState('');
    const [lname, setLname] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [country, setCountry] = useState('United States');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    
    // Password state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // UI state
    const [userData, setUserData] = useState([]);
    const [userRole, setUserRole] = useState('');
    const [showPaymentPopup, setShowPaymentPopup] = useState(false);
    const [accountSummary, setAccountSummary] = useState('');
    const [longAccountSummary, setLongAccountSummary] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [saveLoader, setSaveLoader] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState('');
    const [passwordLoader, setPasswordLoader] = useState(false);
    const [isRedirect, setIsRedirect] = useState(false);
    
    // Error state
    const [errors, setErrors] = useState({
        UserName: '',
        Fname: '',
        Lname: '',
        Phone: '',
        Email: '',
        Address1: '',
        Address2: '',
        Country: '',
        State: '',
        City: '',
        Zip: ''
    });
    
    const [passwordErrors, setPasswordErrors] = useState({
        Current_password: '',
        New_password: '',
        Confirm_password: ''
    });
    
    const noErrors = {
        UserName: '',
        Fname: '',
        Lname: '',
        Phone: '',
        Email: '',
        Address1: '',
        Address2: '',
        Country: '',
        State: '',
        City: '',
        Zip: ''
    };
    
    const noPasswordErrors = {
        Current_password: '',
        New_password: '',
        Confirm_password: ''
    };
    
    const addressData = {
        Country: ['United States'],
        State: [
    { name: 'Alabama', value: 'AL' },
    { name: 'Alaska', value: 'AK' },
    { name: 'Arizona', value: 'AZ' },
    { name: 'Arkansas', value: 'AR' },
    { name: 'California', value: 'CA' },
    { name: 'Colorado', value: 'CO' },
    { name: 'Connecticut', value: 'CT' },
    { name: 'Delaware', value: 'DE' },
    { name: 'Florida', value: 'FL' },
    { name: 'Georgia', value: 'GA' },
    { name: 'Hawaii', value: 'HI' },
    { name: 'Idaho', value: 'ID' },
    { name: 'Illinois', value: 'IL' },
    { name: 'Indiana', value: 'IN' },
    { name: 'Iowa', value: 'IA' },
    { name: 'Kansas', value: 'KS' },
    { name: 'Kentucky', value: 'KY' },
    { name: 'Louisiana', value: 'LA' },
    { name: 'Maine', value: 'ME' },
    { name: 'Maryland', value: 'MD' },
    { name: 'Massachusetts', value: 'MA' },
    { name: 'Michigan', value: 'MI' },
    { name: 'Minnesota', value: 'MN' },
    { name: 'Mississippi', value: 'MS' },
    { name: 'Missouri', value: 'MO' },
    { name: 'Montana', value: 'MT' },
    { name: 'Nebraska', value: 'NE' },
    { name: 'Nevada', value: 'NV' },
    { name: 'New Hampshire', value: 'NH' },
    { name: 'New Jersey', value: 'NJ' },
    { name: 'New Mexico', value: 'NM' },
    { name: 'New York', value: 'NY' },
    { name: 'North Carolina', value: 'NC' },
    { name: 'North Dakota', value: 'ND' },
    { name: 'Ohio', value: 'OH' },
    { name: 'Oklahoma', value: 'OK' },
    { name: 'Oregon', value: 'OR' },
    { name: 'Pennsylvania', value: 'PA' },
    { name: 'Rhode Island', value: 'RI' },
    { name: 'South Carolina', value: 'SC' },
    { name: 'South Dakota', value: 'SD' },
    { name: 'Tennessee', value: 'TN' },
    { name: 'Texas', value: 'TX' },
    { name: 'Utah', value: 'UT' },
    { name: 'Vermont', value: 'VT' },
    { name: 'Virginia', value: 'VA' },
    { name: 'Washington', value: 'WA' },
    { name: 'West Virginia', value: 'WV' },
    { name: 'Wisconsin', value: 'WI' },
    { name: 'Wyoming', value: 'WY' }
        ]
    };

    useEffect(() => {
        try {
            let Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const uid = Usermeta_Data.split(':')[0];
            const uname = Usermeta_Data.split(':')[1];
            const role = localStorage.getItem('UserRole')
                ? window.atob(localStorage.getItem('UserRole'))
                : '';
            setUserRole(role.toLowerCase());
            setTimeout(() => {
                GetUserAccountInfo(uid, uname);
            }, 1200);
            GetAccountNumber(uid, uname);
        } catch {
            logout();
        }
    }, []);

    const logout = () => {
        userService.logout();
        setUserData([]);
    };

    const GetUserAccountInfo = (UserId, UserName) => {
        try {
            userService.GetUserAccountInfo(UserId, UserName).then(apidata => {
                try {
                    if (apidata.length > 0) {
                        if (apidata[0].code !== "-1") {
                            setUserData(apidata);
                            setUserName(apidata[0].username);
                            setUserId(UserId);
                            setFname(apidata[0].fname);
                            setLname(apidata[0].lname);
                            setPhone(apidata[0].phone);
                            setEmail(apidata[0].email);
                            setAddress1(apidata[0].address1);
                            setAddress2(apidata[0].address2);
                            const rawState = apidata[0].stateName || '';
                            // API may return "Please Select State" (empty), a state code ("AL"),
                            // or a full name ("Alabama"). Normalize to code so the dropdown matches.
                            if (!rawState || rawState === 'Please Select State') {
                                setState('');
                            } else {
                                // Try matching by code first, then by full name
                                const match = addressData.State.find(
                                    s => s.value === rawState || s.name === rawState
                                );
                                setState(match ? match.value : rawState);
                            }
                            setCity(apidata[0].cityName);
                            setZip(apidata[0].zipCode);
                        }
            }
        } catch (error) {
                }
            });
        } catch (error) {
        }
    };

    const GetAccountNumber = (UserId, UserName) => {
        try {
            userService.GetAccountNumber(UserId, UserName).then(apidata => {
                if (apidata && apidata.length > 0) {
                    setAccountSummary(apidata[0].accountSummary || '');
                    setLongAccountSummary(apidata[0].longAccountSummary || '');
                }
            });
        } catch (error) {
        }
    };

    const HandleInput = (event) => {
        const { name, value } = event.target;
        let newErrors = { ...errors };
        
        if (name === "Phone") {
            var regex = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;
            if (value.match(regex)) {
                setPhone(value);
                newErrors[name] = '';
                setErrors(newErrors);
            }
        } else if (name === "Fname" || name === "Lname") {
            var special = /^[a-zA-Z]+$/g;
            if (value.match(special) || value === '') {
                if (name === "Fname") setFname(value);
                if (name === "Lname") setLname(value);
                newErrors[name] = '';
                setErrors(newErrors);
            } else {
                return false;
            }
        } else if (name === "City") {
            if (value !== "") {
                let special = /^[a-zA-Z ]*$/;
                if (value.match(special) || value === '') {
                    setCity(value);
                    newErrors[name] = '';
                    setErrors(newErrors);
                } else {
                    return false;
                }
            } else {
                setCity(value);
            }
        } else if (name === "Zip") {
            if (value !== "" || value === "") {
                let special = /^[0-9\b]+$/;
                if (value.match(special) || value === "") {
                    setZip(value);
                    newErrors[name] = '';
                    setErrors(newErrors);
                }
            }
        } else {
            // Handle other fields (UserName, Email, Address1, Address2)
            if (name === "UserName") setUserName(value);
            if (name === "Email") setEmail(value);
            if (name === "Address1") setAddress1(value);
            if (name === "Address2") setAddress2(value);
            newErrors[name] = '';
            setErrors(newErrors);
        }
    };

    const submit_setting = (event) => {
        event.preventDefault();
        setSaveLoader(true);
        let newErrors = { ...errors };
        try {
            userService.GetUserNameFilter(userName).then(apidata => {
                if (apidata.length > 0) {

                    if (apidata[0].code !== '0') {
                        newErrors.UserName = apidata[0].message;
                    }
                    if (userName) {
                        let str = userName;
                        str = str.replace(/^\s+/, '');
                        setUserName(str);
                        if (userName !== "") {
                            var special = /^[0-9\b]+$/;
                            if (userName.match(special)) {
                                newErrors.UserName = 'This Username not allowed';
                            }
                        }
                    }
                    userService.GetPhoneNoValidate(phone).then(apidata => {
                        if (apidata[0].code !== "1") {
                            newErrors.Phone = 'Invalid Phone Number';
                        } else {
                            if (apidata[0].message != null) {
                                setPhone(apidata[0].message);
                            }
                        }

                        // Zod sync validation � overrides API errors for the same fields (matching original behaviour)
                        const profileResult = accountInfoProfileSchema.safeParse({
                            userName, fname, lname, phone, email, address1, city, state, zip,
                        });
                        if (!profileResult.success) {
                            const fe = profileResult.error.flatten().fieldErrors;
                            if (fe.userName?.[0]) newErrors.UserName = fe.userName[0];
                            if (fe.fname?.[0]) newErrors.Fname = fe.fname[0];
                            if (fe.lname?.[0]) newErrors.Lname = fe.lname[0];
                            if (fe.phone?.[0]) newErrors.Phone = fe.phone[0];
                            if (fe.email?.[0]) newErrors.Email = fe.email[0];
                            if (fe.address1?.[0]) newErrors.Address1 = fe.address1[0];
                            if (fe.city?.[0]) newErrors.City = fe.city[0];
                            if (fe.state?.[0]) newErrors.State = fe.state[0];
                            if (fe.zip?.[0]) newErrors.Zip = fe.zip[0];
                        }

                        if (JSON.stringify(newErrors) !== JSON.stringify(noErrors)) {
                            setErrors(newErrors);
                            setSaveLoader(false);
                        } else {
                            SetUserAccountInfo(
                                userId,
                                userName,
                                fname,
                                lname,
                                phone,
                                email,
                                address1,
                                address2,
                                country,
                                state,
                                city,
                                zip
                            );
                        }
                    });
                } else {
                    setSaveLoader(false);
                }
            }).catch(() => {
                setSaveLoader(false);
            });
        } catch (error) {
            setSaveLoader(false);
        }
    };

    const SetUserAccountInfo = (UserId, UserName, Fname, Lname, Phone, Email, Address1, Address2, CountryName, StateName, CityName, ZipCode) => {
        try {
            userService.SetUserAccountInfo(UserId, UserName, Fname, Lname, Phone, Email, Address1, Address2, CountryName, StateName, CityName, ZipCode).then(apidata => {
                if (apidata.length > 0) {
                    if (apidata[0].code !== '-1') {
                        localStorage.setItem('_file_info', window.btoa('0'));
                        setSaveSuccess(true);
                        setSaveMsg("Saved Successfully");
                        setSaveLoader(false);
                        setTimeout(() => {
                            setSaveSuccess(false);
                        }, 2000);
                    } else {
                        setSaveSuccess(true);
                        setSaveMsg(apidata[0].message);
                        setSaveLoader(false);
                setTimeout(() => {
                    setSaveSuccess(false);
                }, 3000);
                    }
            } else {
                    setSaveLoader(false);
            }
            });
        } catch (error) {
        }
    };

    const HandlePasswordInput = (event) => {
        const { name, value } = event.target;
        let newPasswordErrors = { ...passwordErrors };
        
        if (name === "Current_password") setCurrentPassword(value);
        if (name === "New_password") setNewPassword(value);
        if (name === "Confirm_password") setConfirmPassword(value);
        
        newPasswordErrors[name] = '';
        setPasswordErrors(newPasswordErrors);
    };

    const handle_save_password = (event) => {
        event.preventDefault();
        const result = accountPasswordSchema.safeParse({ currentPassword, newPassword, confirmPassword });
        const fe = result.success ? {} : result.error.flatten().fieldErrors;
        const newPasswordErrors = {
            Current_password: fe.currentPassword?.[0] ?? '',
            New_password: fe.newPassword?.[0] ?? '',
            Confirm_password: fe.confirmPassword?.[0] ?? '',
        };
        if (JSON.stringify(newPasswordErrors) !== JSON.stringify(noPasswordErrors)) {
            setPasswordErrors(newPasswordErrors);
        } else {
            setPasswordLoader(true);
            ResetPassword(
                userId,
                userName,
                currentPassword,
                newPassword,
                confirmPassword,
                event
            );
        }
    };

    const ResetPassword = (UserId, UserName, Current_Pwd, New_Pwd, Confirm_New_Pwd, event) => {
        try {
            userService.ResetPassword(UserId, UserName, Current_Pwd, New_Pwd, Confirm_New_Pwd).then(apidata => {
                try {
                    if (apidata.length > 0) {
                        let newPasswordErrors = { ...passwordErrors };
                        if (apidata[0].code === "1") {
                            setPasswordLoader(false);
                setPasswordSuccess(true);
                            setPasswordMsg("Password changed successfully");
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                setTimeout(() => {
                    setPasswordSuccess(false);
                }, 2000);
                        }
                        if (apidata[0].code === "-1") {
                            newPasswordErrors.Current_password = apidata[0].message;
                            setPasswordErrors(newPasswordErrors);
                            setPasswordLoader(false);
                            setPasswordSuccess(true);
                            setPasswordMsg(apidata[0].message);
                            setTimeout(() => {
                                setPasswordSuccess(false);
                            }, 3000);
                        }
                        if (apidata[0].code === "-2") {
                            newPasswordErrors.New_password = apidata[0].message;
                            setPasswordErrors(newPasswordErrors);
                            setPasswordLoader(false);
                            setPasswordSuccess(true);
                            setPasswordMsg(apidata[0].message);
                            setTimeout(() => {
                                setPasswordSuccess(false);
                            }, 3000);
                        }
            }
        } catch (error) {
                }
            });
        } catch (error) {
        }
    };

    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id")) === true)
        return <Navigate to="/signup" />;
    
    if (isRedirect === true)
        return <Navigate to={{ pathname: "/summary" }} />;

    const isLoading = userData.length === 0 || userData == null;

    return (
        <div className="space-y-6">
            {/* Page Header */}
                <div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Account Information</h1>
            </div>

            {/* Loading State */}
            {isLoading ? (
                <Card>
                    <CardContent className="flex items-center justify-center p-4 py-12 sm:p-6 sm:py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-accent" />
                    </CardContent>
                </Card>
            ) : (
                <Tabs defaultValue="profile" className="w-full">
                    <TooltipProvider>
                        <div className="flex justify-center mb-4">
                            <TabsList className="bg-background">
                                <TabsTrigger value="profile" className="flex items-center gap-2">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="flex items-center gap-2">
                                                <User className="h-4 w-4" />
                                                Profile
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Update Profile</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TabsTrigger>
                                <TabsTrigger value="security" className="flex items-center gap-2">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="flex items-center gap-2">
                                                <Lock className="h-4 w-4" />
                                                Security
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Change Password</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TabsTrigger>
                            </TabsList>
                        </div>
                    </TooltipProvider>
                    
                    {/* Profile Tab */}
                    <TabsContent value="profile">
                        <Card>
                            <CardContent className="p-4 sm:p-6 space-y-6">
                                {/* Personal Section */}
                                <section className="space-y-4">
                                    <h3 className="text-2xl font-semibold">Personal</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Username */}
                                        <div className="relative">
                                            <Input
                                                id="username"
                                                type="text"
                                                name="UserName"
                                                value={userName}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.UserName ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="username" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                Username
                                            </Label>
                                            {errors.UserName && (
                                                <p className="text-destructive text-xs mt-1">{errors.UserName}</p>
                                            )}
                                        </div>

                                        {/* First Name */}
                                        <div className="relative">
                                            <Input
                                                id="fname"
                                                type="text"
                                    name="Fname"
                                                value={fname}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.Fname ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="fname" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                First Name
                                            </Label>
                                            {errors.Fname && (
                                                <p className="text-destructive text-xs mt-1">{errors.Fname}</p>
                                            )}
                                        </div>

                                        {/* Last Name */}
                                        <div className="relative">
                                            <Input
                                                id="lname"
                                                type="text"
                                    name="Lname"
                                                value={lname}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.Lname ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="lname" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                Last Name
                                            </Label>
                                            {errors.Lname && (
                                                <p className="text-destructive text-xs mt-1">{errors.Lname}</p>
                                            )}
                                        </div>

                                        {/* Phone */}
                                        <div className="relative">
                                            <Input
                                                id="phone"
                                                type="text"
                                                name="Phone"
                                                value={phone}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.Phone ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="phone" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                Phone
                                            </Label>
                                            {errors.Phone && (
                                                <p className="text-destructive text-xs mt-1">{errors.Phone}</p>
                                            )}
                                        </div>

                                        {/* Email */}
                                        <div className="relative">
                                            <Input
                                                id="email"
                                                type="text"
                                    name="Email"
                                                value={email}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                disabled
                                                className={`peer h-12 ${errors.Email ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="email" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                Email
                                            </Label>
                                            {errors.Email && (
                                                <p className="text-destructive text-xs mt-1">{errors.Email}</p>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                {/* Address Section */}
                                <section className="space-y-4 border-t border-border pt-6">
                                    <h3 className="text-2xl font-semibold">Address</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Street Address */}
                                        <div className="relative">
                                            <Input
                                                id="address1"
                                                type="text"
                                    name="Address1"
                                                value={address1}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.Address1 ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="address1" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                Street Address
                                            </Label>
                                            {errors.Address1 && (
                                                <p className="text-destructive text-xs mt-1">{errors.Address1}</p>
                                            )}
                                        </div>

                                        {/* Address Line 2 */}
                                        <div className="relative">
                                            <Input
                                                id="address2"
                                                type="text"
                                    name="Address2"
                                                value={address2}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.Address2 ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="address2" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                Address Line 2
                                            </Label>
                                            {errors.Address2 && (
                                                <p className="text-destructive text-xs mt-1">{errors.Address2}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* City */}
                                        <div className="relative">
                                            <Input
                                                id="city"
                                                type="text"
                                        name="City"
                                                value={city}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.City ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="city" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                City
                                            </Label>
                                            {errors.City && (
                                                <p className="text-destructive text-xs mt-1">{errors.City}</p>
                                            )}
                                        </div>

                                        {/* State */}
                                        <div className="relative">
                                            <Select
                                                value={state}
                                                onValueChange={(value) => setState(value)}
                                            >
                                                <SelectTrigger id="state" className={`h-12 ${errors.State ? 'border-destructive' : ''}`}>
                                                            <SelectValue placeholder="Select state" />
                                                        </SelectTrigger>
                                                    <SelectContent>
                                                    {addressData.State.map(item => (
                                                        <SelectItem key={item.value} value={item.value}>
                                                            {item.name}
                                                        </SelectItem>
                                                    ))}
                                                    </SelectContent>
                                                </Select>
                                            <Label htmlFor="state" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground">
                                                State *
                                            </Label>
                                            {errors.State && (
                                                <p className="text-destructive text-xs mt-1">{errors.State}</p>
                                            )}
                                        </div>

                                        {/* ZIP Code */}
                                        <div className="relative">
                                            <Input
                                                id="zip"
                                                type="text"
                                        name="Zip"
                                                value={zip}
                                                onChange={HandleInput}
                                                onFocus={HandleInput}
                                                placeholder=" "
                                                className={`peer h-12 ${errors.Zip ? 'border-destructive' : ''}`}
                                            />
                                            <Label htmlFor="zip" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                ZIP / Postal Code *
                                            </Label>
                                            {errors.Zip && (
                                                <p className="text-destructive text-xs mt-1">{errors.Zip}</p>
                                            )}
                                        </div>

                                        {/* Country - Full width */}
                                        <div className="relative md:col-span-3">
                                            <Select
                                                value={country}
                                                onValueChange={(value) => setCountry(value)}
                                                disabled
                                            >
                                                <SelectTrigger id="country" className="h-12">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {addressData.Country.map(item => (
                                                        <SelectItem key={item} value={item}>{item}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Label htmlFor="country" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground">
                                                Country
                                            </Label>
                                        </div>
                                    </div>
                                </section>

                                {/* Payments Section — consumer only */}
                                {userRole === 'consumer' && (
                                    <section className="space-y-4 border-t border-border pt-6">
                                        {accountSummary ? (
                                            <>
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-2xl font-semibold">Payments</h3>
                                                    <button
                                                        type="button"
                                                        className="text-sm text-accent hover:underline font-medium"
                                                        onClick={() => setShowPaymentPopup(true)}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                                <div className="space-y-1 text-sm text-muted-foreground">
                                                    <p>
                                                        Account Number: <span className="font-medium text-foreground">{accountSummary}</span>
                                                    </p>
                                                    <p>
                                                        Text Summary: <span className="font-medium text-foreground">{longAccountSummary}</span>
                                                    </p>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <h3 className="text-2xl font-semibold">Payments</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Manage your bank account details for cashback rewards payouts.
                                                </p>
                                            </>
                                        )}
                                    </section>
                                )}

                                {/* Success/Error Alert for Profile */}
                                {saveSuccess && (
                                    <Alert variant={saveMsg.includes('Successfully') ? 'success' : 'destructive'} className="mt-6">
                                        <CheckCircle className="h-4 w-4" />
                                        <AlertDescription>{saveMsg}</AlertDescription>
                                    </Alert>
                                )}

                                {/* Save Button */}
                                <div className="flex justify-end pt-2">
                                    <Button
                                        variant="secondary"
                                        size="lg"
                                        onClick={submit_setting}
                                        disabled={saveLoader}
                                    >
                                        {saveLoader ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving</> : 'Save Profile Settings'}
                                </Button>
                            </div>
                </CardContent>
            </Card>
                    </TabsContent>
                    
                    {/* Security Tab */}
                    <TabsContent value="security">
                        <Card>
                            <CardContent className="p-4 sm:p-6 space-y-6">
                                <section className="space-y-4">
                                    <h3 className="text-2xl font-semibold">Change Password</h3>
                                    <div className="text-sm text-muted-foreground">
                                        <p className="mb-1">Your new password must:</p>
                                        <ul className="list-disc list-inside space-y-0.5">
                                            <li>Be at least 6 characters in length</li>
                                            <li>Not be same as your current password</li>
                                            <li>Not contain common passwords</li>
                                        </ul>
                                    </div>
                                </section>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Current Password */}
                                    <div className="relative">
                                        <Input
                                            id="current-password"
                                            type={showCurrentPassword ? "text" : "password"}
                                            name="Current_password"
                                            value={currentPassword}
                                            onChange={HandlePasswordInput}
                                            onFocus={HandlePasswordInput}
                                            placeholder=" "
                                            maxLength={30}
                                            className={`peer h-12 pr-10 ${passwordErrors.Current_password ? 'border-destructive' : ''}`}
                                        />
                                        <Label htmlFor="current-password" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                            Current Password
                                        </Label>
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                        >
                                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                        {passwordErrors.Current_password && (
                                            <p className="text-destructive text-xs mt-1">{passwordErrors.Current_password}</p>
                                        )}
                                    </div>

                                    {/* New Password */}
                                    <div className="relative">
                                        <Input
                                            id="new-password"
                                            type={showNewPassword ? "text" : "password"}
                                            name="New_password"
                                            value={newPassword}
                                            onChange={HandlePasswordInput}
                                            onFocus={HandlePasswordInput}
                                            placeholder=" "
                                            maxLength={30}
                                            className={`peer h-12 pr-10 ${passwordErrors.New_password ? 'border-destructive' : ''}`}
                                        />
                                        <Label htmlFor="new-password" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                            New Password
                                        </Label>
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                        >
                                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                        {passwordErrors.New_password && (
                                            <p className="text-destructive text-xs mt-1">{passwordErrors.New_password}</p>
                                        )}
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="relative">
                                        <Input
                                            id="confirm-password"
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="Confirm_password"
                                            value={confirmPassword}
                                            onChange={HandlePasswordInput}
                                            onFocus={HandlePasswordInput}
                                            placeholder=" "
                                            maxLength={30}
                                            className={`peer h-12 pr-10 ${passwordErrors.Confirm_password ? 'border-destructive' : ''}`}
                                        />
                                        <Label htmlFor="confirm-password" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                            Confirm Password
                                        </Label>
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                        {passwordErrors.Confirm_password && (
                                            <p className="text-destructive text-xs mt-1">{passwordErrors.Confirm_password}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Success/Error Alert for Password */}
                    {passwordSuccess && (
                                    <Alert variant={passwordMsg.includes('successfully') ? 'success' : 'destructive'} className="mt-6">
                                        <CheckCircle className="h-4 w-4" />
                                        <AlertDescription>{passwordMsg}</AlertDescription>
                                    </Alert>
                    )}

                                {/* Save Changes Button */}
                                <div className="flex justify-end pt-2">
                                <Button
                                        variant="secondary"
                                        size="lg"
                                        onClick={handle_save_password}
                                        disabled={passwordLoader}
                                    >
                                        {passwordLoader ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving</> : 'Save Changes'}
                                </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            )}

            {/* Edit Bank Account Popup */}
            {showPaymentPopup && (
                <WisePayment
                    type="edit"
                    closePaymentPopup={() => {
                        setShowPaymentPopup(false);
                        // Refresh account summary after editing
                        try {
                            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                            GetAccountNumber(Usermeta_Data.split(':')[0], Usermeta_Data.split(':')[1]);
                        } catch (_) {}
                    }}
                />
            )}
        </div>
    );
};

export default Account_Info_page;


